import React, { useState, useEffect } from 'react';
import { Calendar, CheckCircle2, Star, Trophy, LogOut, Target, ArrowLeft, ArrowRight, X, Check, Clock } from 'lucide-react';
import { logout } from './auth';
import WelcomeModal from './WelcomeModal';
import InteractiveTutorial from './InteractiveTutorial';
import { getAthleteWeekSchedule } from './workout-api';
import { db } from './firebase-config';
import { doc, getDoc, addDoc, collection, serverTimestamp, query, where, getDocs, orderBy, limit } from 'firebase/firestore';

const AthleteTrainingApp = ({ user, onLogout }) => {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [personalBests, setPersonalBests] = useState([]);
  const [activeTab, setActiveTab] = useState('schedule');
  const [loading, setLoading] = useState(true);
  const [weeklySchedule, setWeeklySchedule] = useState(null);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [completedWorkouts, setCompletedWorkouts] = useState({});
  const [workoutHistory, setWorkoutHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    effort_level: 5,
    performance: '',
    notes: '',
    is_personal_best: false
  });

  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem(`welcome_seen_${user?.uid}`);
    if (user && !hasSeenWelcome) setShowWelcome(true);
    setLoading(false);
    loadPersonalBests();
    loadCompletedWorkouts();
  }, [user, currentWeek]);

  const handleWelcomeClose = () => {
    localStorage.setItem(`welcome_seen_${user?.uid}`, 'true');
    setShowWelcome(false);
  };

  const handleStartTour = () => {
    localStorage.setItem(`welcome_seen_${user?.uid}`, 'true');
    setShowWelcome(false);
    setShowTutorial(true);
  };

  const handleTutorialComplete = () => {
    localStorage.setItem(`tutorial_completed_${user?.uid}`, 'true');
    setShowTutorial(false);
  };

  const loadPersonalBests = async () => {
    if (!user) return;
    try {
      const q = query(
        collection(db, 'workout_feedback'),
        where('athlete_id', '==', user.uid),
        where('is_personal_best', '==', true),
        orderBy('completed_at', 'desc')
      );
      const snapshot = await getDocs(q);
      setPersonalBests(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error loading personal bests:', error);
    }
  };

  const loadCompletedWorkouts = async () => {
    if (!user) return;
    try {
      const weekStart = new Date(currentWeek);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const q = query(
        collection(db, 'workout_feedback'),
        where('athlete_id', '==', user.uid),
        where('completed_at', '>=', weekStart),
        where('completed_at', '<=', weekEnd)
      );
      const snapshot = await getDocs(q);
      const completed = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const date = data.completed_at.toDate();
        const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()];
        completed[dayName] = { ...data, id: doc.id };
      });
      setCompletedWorkouts(completed);
    } catch (error) {
      console.error('Error loading completed workouts:', error);
    }
  };

  const loadWorkoutHistory = async (workoutName) => {
    if (!user || !workoutName) return;
    setIsLoadingHistory(true);
    try {
      const q = query(
        collection(db, 'workout_feedback'),
        where('athlete_id', '==', user.uid),
        where('workout_name', '==', workoutName),
        orderBy('completed_at', 'desc'),
        limit(5)
      );
      const snapshot = await getDocs(q);
      setWorkoutHistory(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error loading workout history:', error);
      setWorkoutHistory([]);
    }
    setIsLoadingHistory(false);
  };

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) onLogout();
  };

  const getWeekDates = () => {
    const start = new Date(currentWeek);
    start.setDate(start.getDate() - start.getDay());
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const navigateWeek = (direction) => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(newWeek.getDate() + (direction * 7));
    setCurrentWeek(newWeek);
  };

  const loadWeeklySchedule = async () => {
    if (!user) return;
    setIsLoadingSchedule(true);
    const weekStart = new Date(currentWeek);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    try {
      const scheduleData = await getAthleteWeekSchedule(user.uid, weekStart, weekEnd);
      if (scheduleData && scheduleData.workouts) {
        const templateIds = Object.values(scheduleData.workouts).filter(Boolean);
        if (templateIds.length > 0) {
          const templatePromises = templateIds.map(async (templateId) => {
            try {
              const templateDoc = await getDoc(doc(db, 'workout_templates', templateId));
              return templateDoc.exists() ? { id: templateId, ...templateDoc.data() } : null;
            } catch (error) {
              return { id: templateId, name: 'Unknown Workout' };
            }
          });
          const templates = await Promise.all(templatePromises);
          const templateMap = {};
          templates.filter(Boolean).forEach(template => { templateMap[template.id] = template; });
          const namedWorkouts = {};
          Object.keys(scheduleData.workouts).forEach(day => {
            const templateId = scheduleData.workouts[day];
            if (templateId && templateMap[templateId]) {
              namedWorkouts[day] = templateMap[templateId];
            } else {
              namedWorkouts[day] = templateId;
            }
          });
          setWeeklySchedule({ ...scheduleData, workouts: namedWorkouts });
        } else {
          setWeeklySchedule(scheduleData);
        }
      } else {
        setWeeklySchedule(scheduleData);
      }
    } catch (error) {
      console.error('Error loading schedule:', error);
    }
    setIsLoadingSchedule(false);
  };

  useEffect(() => {
    loadWeeklySchedule();
  }, [user, currentWeek]);

  const handleWorkoutClick = async (dayName, index) => {
    const workout = weeklySchedule?.workouts?.[dayName.toLowerCase()];
    if (!workout) return;
    const workoutName = typeof workout === 'object' ? workout.name : workout;
    setSelectedWorkout({
      day: dayName,
      name: workoutName,
      prescription: typeof workout === 'object' ? workout.prescription : null,
      date: getWeekDates()[index]
    });
    setShowFeedbackModal(true);
    await loadWorkoutHistory(workoutName);
  };

  const handleCompleteFeedback = async () => {
    if (!selectedWorkout || !user) return;
    try {
      const feedbackData = {
        athlete_id: user.uid,
        athlete_name: user.name || user.email,
        coach_id: user.coach_id || 'unknown',
        workout_name: selectedWorkout.name,
        workout_day: selectedWorkout.day,
        effort_level: feedbackForm.effort_level,
        performance: feedbackForm.performance || '',
        notes: feedbackForm.notes || '',
        is_personal_best: feedbackForm.is_personal_best,
        completed_at: serverTimestamp(),
        workout_date: selectedWorkout.date
      };
      await addDoc(collection(db, 'workout_feedback'), feedbackData);
      setCompletedWorkouts(prev => ({
        ...prev,
        [selectedWorkout.day.toLowerCase()]: { ...feedbackData, completed_at: new Date() }
      }));
      if (feedbackForm.is_personal_best) loadPersonalBests();
      setFeedbackForm({ effort_level: 5, performance: '', notes: '', is_personal_best: false });
      setWorkoutHistory([]);
      setShowFeedbackModal(false);
      setSelectedWorkout(null);
      alert('✅ Workout completed! Feedback sent to your coach.');
    } catch (error) {
      if (error.code === 'permission-denied') {
        alert('❌ Permission denied. Please contact your coach to ensure proper setup.');
      } else {
        alert(`❌ Error submitting feedback: ${error.message}`);
      }
    }
  };

  const weekDates = getWeekDates();
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
      padding: '24px',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(20px)',
          borderRadius: '24px', padding: '32px', marginBottom: '24px',
          border: '1px solid rgba(37, 99, 235, 0.1)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <img src="/coach-logo.png" alt="Coach's Log" style={{ width: '60px', height: '60px', borderRadius: '12px' }} />
              <div>
                <h1 style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', margin: 0 }}>
                  <span style={{ color: '#2563eb' }}>Coach's</span> Log
                </h1>
                <p style={{ color: '#64748b', margin: '4px 0 0 0', fontWeight: '500', fontSize: '14px' }}>
                  Training Journal • Welcome, {user?.name || user?.email}
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              {[
                { id: 'schedule', label: 'Schedule', icon: Calendar },
                { id: 'bests', label: `Personal Bests (${personalBests.length})`, icon: Trophy }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '12px 16px',
                    background: activeTab === tab.id ? 'linear-gradient(135deg, #2563eb, #3b82f6)' : 'white',
                    color: activeTab === tab.id ? 'white' : '#64748b',
                    border: '1px solid #e2e8f0', borderRadius: '10px',
                    cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                    textTransform: 'uppercase', letterSpacing: '0.05em'
                  }}
                >
                  <tab.icon size={16} />
                  {tab.label}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                onMouseEnter={() => setShowHelp(true)}
                onMouseLeave={() => setShowHelp(false)}
                style={{
                  padding: '12px', background: '#f8fafc', color: '#64748b',
                  border: '1px solid #e2e8f0', borderRadius: '12px',
                  cursor: 'pointer', fontSize: '18px', position: 'relative'
                }}
              >
                ❓
                {showHelp && (
                  <div style={{
                    position: 'fixed', top: '70px', right: '120px',
                    background: 'white', border: '1px solid #e2e8f0',
                    borderRadius: '12px', padding: '8px', minWidth: '200px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', zIndex: 9999
                  }}
                  onMouseEnter={() => setShowHelp(true)}
                  onMouseLeave={() => setShowHelp(false)}
                  onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => { setShowTutorial(true); setShowHelp(false); }}
                      style={{
                        width: '100%', padding: '12px', background: 'transparent',
                        border: 'none', color: '#374151', textAlign: 'left',
                        cursor: 'pointer', borderRadius: '8px', fontSize: '14px', fontWeight: '500'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >
                      🎓 Take Tutorial
                    </button>
                    <a
                      href="mailto:tim@foundationalsports.com"
                      style={{
                        display: 'block', width: '100%', padding: '12px',
                        background: 'transparent', color: '#374151',
                        textAlign: 'left', textDecoration: 'none',
                        borderRadius: '8px', fontSize: '14px', fontWeight: '500'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >
                      📧 Contact Support
                    </a>
                  </div>
                )}
              </button>

              <button
                onClick={handleLogout}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '12px 24px', background: '#f8fafc', color: '#64748b',
                  border: '1px solid #e2e8f0', borderRadius: '12px',
                  cursor: 'pointer', fontWeight: '600', fontSize: '14px',
                  textTransform: 'uppercase', letterSpacing: '0.05em'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#dc2626';
                  e.target.style.color = '#dc2626';
                  e.target.style.background = '#fef2f2';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.color = '#64748b';
                  e.target.style.background = '#f8fafc';
                }}
              >
                <LogOut size={20} /> Logout
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(20px)',
          borderRadius: '24px', padding: '40px',
          border: '1px solid rgba(37, 99, 235, 0.1)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05)', minHeight: '60vh'
        }}>

          {activeTab === 'schedule' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                <button onClick={() => navigateWeek(-1)} style={{ padding: '12px 20px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', fontSize: '14px' }}>
                  <ArrowLeft size={18} /> Previous Week
                </button>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
                  Week of {weekDates[0]?.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                </h2>
                <button onClick={() => navigateWeek(1)} style={{ padding: '12px 20px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', fontSize: '14px' }}>
                  Next Week <ArrowRight size={18} />
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '32px' }}>
                {weekDates.map((date, index) => {
                  const dayName = dayNames[index];
                  const workoutData = weeklySchedule?.workouts?.[dayName.toLowerCase()];
                  const workoutName = typeof workoutData === 'object' ? workoutData?.name : workoutData;
                  const isCompleted = completedWorkouts[dayName.toLowerCase()];
                  const isPersonalBest = isCompleted?.is_personal_best;

                  return (
                    <div
                      key={date.toISOString()}
                      onClick={() => handleWorkoutClick(dayName, index)}
                      style={{
                        background: 'white', borderRadius: '16px', padding: '24px',
                        border: isCompleted ? '1px solid #10b981' : '1px solid #e2e8f0',
                        transition: 'all 0.3s', position: 'relative', cursor: 'pointer',
                        boxShadow: isCompleted ? '0 4px 6px -1px rgba(16, 185, 129, 0.1)' : '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                      }}
                      onMouseEnter={(e) => {
                        if (!isCompleted) {
                          e.currentTarget.style.borderColor = '#2563eb';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isCompleted) {
                          e.currentTarget.style.borderColor = '#e2e8f0';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      {isPersonalBest && (
                        <div style={{ position: 'absolute', top: '16px', right: '80px', color: '#f59e0b', fontSize: '20px' }}>⭐</div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0 }}>{dayName}</h3>
                        <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '600' }}>
                          {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                      <div style={{
                        padding: '20px',
                        background: workoutName ? (isCompleted ? '#f0fdf4' : '#f0f9ff') : '#f8fafc',
                        borderRadius: '12px',
                        border: workoutName ? (isCompleted ? '1px solid #bbf7d0' : '1px solid #bfdbfe') : '1px dashed #cbd5e1',
                        textAlign: 'center',
                        marginBottom: workoutName && !isCompleted ? '16px' : '0'
                      }}>
                        {isCompleted ? (
                          <CheckCircle2 size={32} style={{ margin: '0 auto 12px', color: '#10b981' }} />
                        ) : (
                          <Target size={32} style={{ margin: '0 auto 12px', color: workoutName ? '#2563eb' : '#cbd5e1' }} />
                        )}
                        <p style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 8px 0', color: isCompleted ? '#065f46' : (workoutName ? '#1e293b' : '#64748b') }}>
                          {isLoadingSchedule ? 'Loading...' : workoutName ? (isCompleted ? 'Completed!' : `Workout: ${workoutName}`) : 'No workout scheduled'}
                        </p>
                        {isCompleted && (
                          <div style={{ fontSize: '12px', color: '#065f46', marginTop: '8px' }}>
                            <div>Effort: {isCompleted.effort_level}/10</div>
                            {isCompleted.performance && <div style={{ marginTop: '4px' }}>⏱ {isCompleted.performance}</div>}
                            {isCompleted.notes && <div style={{ fontStyle: 'italic', marginTop: '4px' }}>"{isCompleted.notes}"</div>}
                          </div>
                        )}
                      </div>
                      {workoutName && !isCompleted && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleWorkoutClick(dayName, index); }}
                          style={{
                            width: '100%', padding: '12px',
                            background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                            border: 'none', borderRadius: '10px', color: 'white',
                            fontWeight: '600', fontSize: '14px', cursor: 'pointer',
                            textTransform: 'uppercase', letterSpacing: '0.05em'
                          }}
                        >
                          Complete Workout
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div style={{ background: 'white', borderRadius: '20px', padding: '32px', border: '1px solid #e2e8f0' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Trophy style={{ color: '#2563eb' }} size={24} /> Week Overview
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                  <div style={{ padding: '20px', background: '#f0f9ff', borderRadius: '12px', border: '1px solid #e0f2fe', textAlign: 'center' }}>
                    <div style={{ fontSize: '32px', fontWeight: '800', color: '#2563eb', marginBottom: '8px' }}>
                      {weeklySchedule ? Object.values(weeklySchedule.workouts || {}).filter(Boolean).length : 0}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#64748b' }}>Workouts Scheduled</div>
                  </div>
                  <div style={{ padding: '20px', background: '#f0fdf4', borderRadius: '12px', border: '1px solid #dcfce7', textAlign: 'center' }}>
                    <div style={{ fontSize: '32px', fontWeight: '800', color: '#16a34a', marginBottom: '8px' }}>
                      {Object.keys(completedWorkouts).length}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#64748b' }}>Workouts Completed</div>
                  </div>
                  <div style={{ padding: '20px', background: '#fef3c7', borderRadius: '12px', border: '1px solid #fde68a', textAlign: 'center' }}>
                    <div style={{ fontSize: '32px', fontWeight: '800', color: '#d97706', marginBottom: '8px' }}>
                      {Object.values(completedWorkouts).filter(w => w.is_personal_best).length}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#64748b' }}>Personal Bests</div>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'bests' && (
            <div>
              <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '32px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Trophy style={{ color: '#f59e0b' }} size={32} /> Personal Bests ({personalBests.length})
              </h2>
              {personalBests.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                  {personalBests.map(best => (
                    <div key={best.id} style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid #f59e0b' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0 }}>{best.workout_name}</h3>
                        <div style={{ color: '#f59e0b', fontSize: '20px' }}>⭐</div>
                      </div>
                      <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                        {best.completed_at?.toDate().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                      </div>
                      <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>Effort Level: {best.effort_level}/10</div>
                      {best.performance && (
                        <div style={{ fontSize: '14px', color: '#2563eb', fontWeight: '600', marginBottom: '8px' }}>⏱ {best.performance}</div>
                      )}
                      {best.notes && (
                        <p style={{ fontSize: '14px', color: '#1e293b', fontStyle: 'italic', margin: 0, lineHeight: '1.5' }}>"{best.notes}"</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '80px', color: '#94a3b8' }}>
                  <Trophy size={64} style={{ margin: '0 auto 16px', color: '#cbd5e1' }} />
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>No Personal Bests Yet</h3>
                  <p style={{ fontSize: '16px', fontWeight: '500' }}>Complete workouts and mark your achievements with the star!</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && selectedWorkout && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '24px'
        }} onClick={() => setShowFeedbackModal(false)}>
          <div style={{
            background: 'white', borderRadius: '24px', padding: '40px',
            maxWidth: '540px', width: '100%', maxHeight: '90vh', overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '24px' }}>
              <div style={{ flex: 1, marginRight: '16px' }}>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px 0' }}>Complete Workout</h2>
                <p style={{ fontSize: '16px', color: '#64748b', margin: 0 }}>{selectedWorkout.day} - {selectedWorkout.name}</p>
                {selectedWorkout.prescription && (
                  <div style={{ marginTop: '16px', padding: '16px', background: '#f0f9ff', borderRadius: '12px', border: '1px solid #bfdbfe' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                      Workout Details
                    </div>
                    <p style={{ fontSize: '14px', color: '#1e293b', margin: 0, lineHeight: '1.6', fontWeight: '500' }}>
                      {selectedWorkout.prescription}
                    </p>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowFeedbackModal(false)}
                style={{ padding: '8px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#64748b', cursor: 'pointer', flexShrink: 0 }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Previous Performances */}
            {(isLoadingHistory || workoutHistory.length > 0) && (
              <div style={{ marginBottom: '24px', padding: '16px', background: '#fefce8', borderRadius: '12px', border: '1px solid #fde68a' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Clock size={14} /> Previous Performances
                </div>
                {isLoadingHistory ? (
                  <div style={{ fontSize: '14px', color: '#92400e' }}>Loading history...</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {workoutHistory.map((entry) => (
                      <div key={entry.id} style={{ padding: '10px 12px', background: 'white', borderRadius: '8px', border: '1px solid #fde68a', fontSize: '13px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: entry.performance || entry.notes ? '4px' : '0' }}>
                          <span style={{ fontWeight: '600', color: '#1e293b' }}>
                            {entry.completed_at?.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                          <span style={{ color: '#64748b', fontSize: '12px' }}>Effort: {entry.effort_level}/10 {entry.is_personal_best ? '⭐' : ''}</span>
                        </div>
                        {entry.performance && (
                          <div style={{ color: '#2563eb', fontWeight: '600' }}>⏱ {entry.performance}</div>
                        )}
                        {entry.notes && (
                          <div style={{ color: '#64748b', fontStyle: 'italic' }}>"{entry.notes}"</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Performance Input */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
                Performance / Result <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '400' }}>(optional)</span>
              </label>
              <input
                type="text"
                value={feedbackForm.performance}
                onChange={(e) => setFeedbackForm({...feedbackForm, performance: e.target.value})}
                placeholder="e.g. avg 1:45 per rep, 3 sets of 10 @ 135lbs"
                style={{
                  width: '100%', padding: '12px', borderRadius: '12px',
                  background: '#f8fafc', border: '1px solid #e2e8f0',
                  color: '#1e293b', fontSize: '14px', fontWeight: '500',
                  outline: 'none', fontFamily: 'inherit'
                }}
                onFocus={(e) => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            {/* Effort Level */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>
                How hard was this workout? (1-10)
              </label>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                {[1,2,3,4,5,6,7,8,9,10].map(level => (
                  <button
                    key={level}
                    onClick={() => setFeedbackForm({...feedbackForm, effort_level: level})}
                    style={{
                      width: '40px', height: '40px', borderRadius: '8px', border: '2px solid',
                      borderColor: feedbackForm.effort_level === level ? '#2563eb' : '#e2e8f0',
                      background: feedbackForm.effort_level === level ? '#2563eb' : 'white',
                      color: feedbackForm.effort_level === level ? 'white' : '#64748b',
                      fontWeight: '600', cursor: 'pointer'
                    }}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
                Notes <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '400' }}>(optional)</span>
              </label>
              <textarea
                value={feedbackForm.notes}
                onChange={(e) => setFeedbackForm({...feedbackForm, notes: e.target.value})}
                placeholder="How did the workout feel? Any observations?"
                rows={3}
                style={{
                  width: '100%', padding: '12px', borderRadius: '12px',
                  background: '#f8fafc', border: '1px solid #e2e8f0',
                  color: '#1e293b', fontSize: '14px', fontWeight: '500',
                  outline: 'none', resize: 'vertical', fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Personal Best */}
            <div style={{ marginBottom: '32px' }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', padding: '16px',
                background: feedbackForm.is_personal_best ? '#fef3c7' : '#f8fafc',
                border: `1px solid ${feedbackForm.is_personal_best ? '#f59e0b' : '#e2e8f0'}`,
                borderRadius: '12px'
              }}>
                <input
                  type="checkbox"
                  checked={feedbackForm.is_personal_best}
                  onChange={(e) => setFeedbackForm({...feedbackForm, is_personal_best: e.target.checked})}
                  style={{ display: 'none' }}
                />
                <div style={{
                  width: '24px', height: '24px', borderRadius: '6px',
                  border: `2px solid ${feedbackForm.is_personal_best ? '#f59e0b' : '#cbd5e1'}`,
                  background: feedbackForm.is_personal_best ? '#f59e0b' : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {feedbackForm.is_personal_best && <Check size={14} style={{ color: 'white' }} />}
                </div>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Star size={16} style={{ color: '#f59e0b' }} /> Mark as Personal Best
                  </div>
                  <div style={{ fontSize: '14px', color: '#64748b', marginTop: '2px' }}>Did you achieve something special today?</div>
                </div>
              </label>
            </div>

            <button
              onClick={handleCompleteFeedback}
              style={{
                width: '100%', padding: '16px',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                border: 'none', borderRadius: '12px', color: 'white',
                fontWeight: '700', fontSize: '16px', textTransform: 'uppercase',
                letterSpacing: '0.05em', cursor: 'pointer'
              }}
            >
              ✅ Complete Workout
            </button>
          </div>
        </div>
      )}

      {showWelcome && <WelcomeModal user={user} onClose={handleWelcomeClose} onStartTour={handleStartTour} />}
      {showTutorial && <InteractiveTutorial user={user} onComplete={handleTutorialComplete} />}
    </div>
  );
};

export default AthleteTrainingApp;
